<template>
  <div class="password-change">
    <div
      v-if="requireOldPassword"
      class="password-change__input"
    >
      <InputField
        name="oldPassword"
        type="password"
        label="Current password"
        required
        autocomplete="current-password"
        :error="error.oldPassword"
        :model-value="oldPassword"
        @update:model-value="$emit('update:old-password', $event)"
      />
    </div>
    <div class="password-change__input">
      <PasswordInput
        autocomplete="new-password"
        label="New password"
        :error="error.password"
        :model-value="password"
        @update:model-value="$emit('update:password', $event)"
      />
    </div>
    <div class="password-change__input">
      <InputField
        name="passwordConfirmation"
        type="password"
        label="Confirm new password"
        required
        autocomplete="new-password"
        :error="error.passwordConfirmation"
        :model-value="passwordConfirmation"
        @update:model-value="$emit('update:password-confirmation', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'PasswordChange',
}
</script>

<script lang="ts" setup>
import isEmpty from 'validator/lib/isEmpty'
import { computed, ref, watch } from 'vue'

import InputField from '@/components/Common/InputField/V2/InputField.vue'
import PasswordInput from '@/components/Common/PasswordInput/PasswordInput.vue'
import { validatePassword } from '@/core/utils/validation'

type PasswordChangeError = {
  oldPassword?: string
  password?: string
  passwordConfirmation?: string
}

const props = withDefaults(
  defineProps<{
    password: string
    passwordConfirmation: string
    requireOldPassword?: boolean
    oldPassword?: string
  }>(),
  {
    requireOldPassword: false,
    oldPassword: '',
  },
)

defineEmits<{
  (e: 'update:old-password', value: string): void
  (e: 'update:password', value: string): void
  (e: 'update:password-confirmation', value: string): void
}>()

const error = ref<PasswordChangeError>({})

const isValid = computed(() => Object.keys(error.value).length === 0)

const onOldPasswordChange = (): void => {
  error.value.oldPassword = ''
}

const onPasswordChange = (): void => {
  error.value.password = ''
}

const onPasswordConfirmationChange = (): void => {
  error.value.passwordConfirmation = ''
}

const validateForm = (): boolean => {
  error.value = {}

  const passwordIssues = validatePassword(props.password)
  if (passwordIssues.length > 0) {
    error.value.password = passwordIssues[0]
  }
  if (isEmpty(props.passwordConfirmation)) {
    error.value.passwordConfirmation = 'Confirm password cannot be empty'
  } else if (props.password !== props.passwordConfirmation) {
    error.value.passwordConfirmation = 'You must enter the same password'
  }

  return Object.keys(error.value).length === 0
}

const setErrors = (newError: PasswordChangeError): void => {
  if (!newError) {
    return
  }
  error.value = {}
  if (newError.oldPassword) {
    error.value.oldPassword = newError.oldPassword
  }
  if (newError.password) {
    error.value.password = newError.password
  }
  if (newError.passwordConfirmation) {
    error.value.passwordConfirmation = newError.passwordConfirmation
  }
}

defineExpose({
  isValid,
  validateForm,
  setErrors,
})

watch(() => props.oldPassword, onOldPasswordChange)
watch(() => props.password, onPasswordChange)
watch(() => props.passwordConfirmation, onPasswordConfirmationChange)
</script>

<style lang="scss" scoped>
.password-change {
  width: 100%;
  @include col--center;
}

.password-change__input {
  width: 100%;
  padding: 8px 0;
}
</style>
